import * as Styled from "./Header.styles";

import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { GatherLogo, LogoDivider, SlalomLogoBlack } from "../../assets";
import PopupState, { bindHover, bindPopover } from "material-ui-popup-state";

import AvatarMenu from "../AvatarMenu/AvatarMenu";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import HoverPopover from "material-ui-popup-state/HoverPopover";
import { Link } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import NavigationMenu from "../NavigationMenu/NavigationMenu";
import SearchIcon from "@mui/icons-material/Search";
import SearchMenu from "../SearchMenu/SearchMenu";
import { UserState } from "../../types/redux";
import { searchEvents } from "../../store/actions/searchActions";
import { useAppDispatch } from "../../store/hooks";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { analyticsLink } from "../../constants/links";

interface Page {
  label: string;
  path: string;
  protected: boolean;
}

enum DrawerType {
  None,
  Search,
  Avatar,
  Menu,
}

const Header = () => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [activeDrawer, setActiveDrawer] = useState(DrawerType.None);

  const handleDrawerOpen = (drawerType: DrawerType) => {
    setActiveDrawer(drawerType);
  };

  const handleDrawerClose = () => {
    setActiveDrawer(DrawerType.None);
  };

  const [scrollPosition, setScrollPosition] = useState(0);

  const handleScroll = () => {
    setScrollPosition(window.pageYOffset);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const userState: UserState = useSelector((state: any) => state.user);

  const [inputValue, setInputValue] = useState("");
  const [filter, setFilter] = useState("ACTIVE");
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  const isDesktop = useMediaQuery((theme: any) =>
    theme.breakpoints.up("desktop")
  );

  const renderLogo = () => (
    <>
      <Link to="/events">
        <img src={GatherLogo} alt="gather-logo" height={24} />
      </Link>
      <Styled.LogoDivider src={LogoDivider} alt="logo-divider" />
      <Link to="/events">
        <img src={SlalomLogoBlack} alt="slalom-logo" height={24} />
      </Link>
    </>
  );

  useEffect(() => {
    if (inputValue !== "") {
      dispatch(searchEvents({ term: inputValue, filter }));
    }
  }, [inputValue, filter, dispatch]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const term = event.target.value;
      setInputValue(term);
      dispatch(searchEvents({ term, filter }));

      if (term && activeDrawer !== DrawerType.Search) {
        // Open the drawer only if it's not already open and the input has a value
        handleDrawerOpen(DrawerType.Search);
      } else if (!term && isDesktop) {
        // Close the drawer if input is empty and on desktop
        handleDrawerClose();
      }
    },
    [dispatch, activeDrawer, isDesktop, filter]
  );

  useEffect(() => {
    if (activeDrawer === DrawerType.Search && inputValue) {
      // Use setTimeout to delay the refocusing of the input field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      // Ensure the input loses focus when closing the drawer
      inputRef.current?.blur();
    }
  }, [activeDrawer, inputValue]);

  useEffect(() => {
    if (
      isDesktop &&
      (activeDrawer === DrawerType.Menu || activeDrawer === DrawerType.Avatar)
    ) {
      handleDrawerClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  useEffect(() => {
    if (
      (userState.role === "ADMIN" || userState.role === "ADMIN:SUPER") &&
      !userState.loading &&
      userState.success
    ) {
      setIsUserAdmin(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState.role, userState.loading, userState.success]);

  const pages: Page[] = [
    { label: "Home", path: "/events", protected: false },
    { label: "Manage Events", path: "/manage", protected: true },
    // { label: "Analytics", path: analyticsLink, protected: true },
    { label: "FAQ", path: "/faq", protected: false },
  ];

  return (
    <Styled.HeaderContainer>
      {isDesktop ? (
        <Box
          sx={{
            display: "flex",
            gap: theme.spacing(4),
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          {pages?.map((page: any, index: any) => {
            const isActive = location.pathname === page.path;
            return (
              ((isUserAdmin && page.protected) || !page.protected) && (
                <Link
                  key={index}
                  to={page.path}
                  style={{ textDecoration: "none" }}
                  onClick={() => {
                    handleDrawerClose();
                    setTimeout(() => {
                      if (inputRef.current) {
                        inputRef.current.blur();
                      }
                    }, 0);
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "black",
                      fontFamily: "Slalom Sans",
                      fontWeight: 400,
                      marginRight: theme.spacing(1),
                      textAlign: "center",
                      transition: "background-color 0.5s ease, padding 0.5s ease",
                      backgroundColor: isActive
                        ? theme.colors.secondary.chartreuse
                        : "transparent",
                      padding: isActive
                        ? `${theme.spacing(2)} ${theme.spacing(3)}`
                        : `${theme.spacing(1)} ${theme.spacing(1)}`,
                      borderRadius: theme.spacing(4),
                      "&:hover": {
                        fontFamily: "Slalom Sans Bold",
                        fontWeight: 700
                      },
                    }}
                  >
                    {page.label}
                  </Typography>
                </Link>
              )
            );
          })}
          {/* This is a temporary fix for now, needs to be a part of pages */}
          {userState.role === "ADMIN:SUPER" &&
            <Link
              key={'Analytics Link'}
              to={analyticsLink}
              style={{ textDecoration: "none" }}
              target="_blank"
              onClick={() => {
                handleDrawerClose();
                setTimeout(() => {
                  if (inputRef.current) {
                    inputRef.current.blur();
                  }
                }, 0);
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: "black",
                  fontFamily: "Slalom Sans Bold",
                  marginRight: theme.spacing(1),
                  textAlign: "center",
                  transition:
                    "background-color 0.5s ease, padding 0.5s ease",
                  backgroundColor:
                    location.pathname === analyticsLink
                      ? theme.colors.secondary.chartreuse
                      : "transparent",
                  padding:
                    location.pathname === analyticsLink
                      ? `${theme.spacing(2)} ${theme.spacing(3)}`
                      : `${theme.spacing(1)} ${theme.spacing(1)}`,
                  borderRadius: theme.spacing(4),
                }}
              >
                {'Analytics'}
              </Typography>
            </Link>
          }
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: theme.spacing(2),
            alignItems: "center",
            justifyContent: "flex-start",
            marginRight: theme.spacing(1),
            flex: "0 0 auto",
            gridColumn: { xs: "1 / 2" },
          }}
        >
          <IconButton
            onClick={() => {
              if (activeDrawer === DrawerType.Menu) {
                handleDrawerClose();
              } else {
                handleDrawerOpen(DrawerType.Menu);
              }
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: theme.spacing(2),
          justifyContent: "center",
          alignItems: "center",
        }}
      >
      </Box>
      <Styled.LogoWrapper>
        {renderLogo()}
      </Styled.LogoWrapper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: theme.spacing(4),
          gridColumn: { xs: "3 / 4" },
        }}
      >
        {isDesktop && (
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: "Slalom Sans", fontSize: 14, color: "grey" }}
          >
            Notice something?{" "}
            <Styled.FeedbackLink
              href="https://forms.office.com/r/GzKxMQkDFN"
              target="_blank"
              rel="noopener noreferrer"
            >
              Give Gather Feedback
            </Styled.FeedbackLink>
          </Typography>
        )}

        {activeDrawer === DrawerType.Search ? (
          <Styled.CloseIconButton
            onClick={handleDrawerClose}
            aria-label="Close search"
            disableRipple
            disableFocusRipple
            disableTouchRipple
          >
            <CloseIcon />
          </Styled.CloseIconButton>
        ) : (
          isDesktop ? (
            <Styled.SearchIconButton
              onClick={() => handleDrawerOpen(DrawerType.Search)}
              aria-label="Open search"
              disableRipple
              disableFocusRipple
              disableTouchRipple
            >
              <SearchIcon />
            </Styled.SearchIconButton>
          ) : (
            <IconButton
              onClick={() => handleDrawerOpen(DrawerType.Search)}
              aria-label="Open search"
              sx={{ p: 0 }}
            >
              <SearchIcon sx={{ color: theme.colors.primary.slalomBlue }} />
            </IconButton>
          )
        )}

        {isDesktop ? (
          <PopupState variant="popover" popupId="popup-popover">
            {(popupState) => (
              <>
                <Styled.AvatarButtonDesktop sx={{ ml: 0 }} {...bindHover(popupState)}>
                  <Styled.Avatar alt="user-avatar" src="" {...bindHover(popupState)} />
                </Styled.AvatarButtonDesktop>
                <HoverPopover {...bindPopover(popupState)} /* ... */>
                  <AvatarMenu user={userState} />
                </HoverPopover>
              </>
            )}
          </PopupState>
        ) : (
          <Styled.AvatarButtonMobile
            onClick={() =>
              activeDrawer === DrawerType.Avatar ? handleDrawerClose() : handleDrawerOpen(DrawerType.Avatar)
            }
          >
            <Styled.Avatar alt="user-avatar" src="" />
          </Styled.AvatarButtonMobile>
        )}
      </Box>
      <Drawer
        anchor="top"
        open={activeDrawer === DrawerType.Menu}
        onClose={handleDrawerClose}
        sx={{
          "&.MuiDrawer-root .MuiDrawer-paper": { marginTop: 100 - scrollPosition + "px" },
          zIndex: theme.zIndex.drawer,
        }}
      >
        <NavigationMenu
          pages={pages.filter((page) => (isUserAdmin && page.protected) || !page.protected)}
          toggleDrawer={handleDrawerClose}
        />
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" sx={{ color: "grey", fontFamily: "Slalom Sans" }}>
            Notice something?{" "}
            <Styled.FeedbackLink
              href="https://forms.office.com/r/GzKxMQkDFN"
              target="_blank"
              rel="noopener noreferrer"
            >
              Give Gather Feedback
            </Styled.FeedbackLink>
          </Typography>
        </Box>
      </Drawer>
      <Drawer
        anchor="top"
        open={activeDrawer === DrawerType.Avatar}
        onClose={handleDrawerClose}
        sx={{
          "&.MuiDrawer-root .MuiDrawer-paper": {
            marginTop: 100 - scrollPosition + "px",
          },
          zIndex: theme.zIndex.drawer,
        }}
      >
        <AvatarMenu user={userState} />
      </Drawer>
      <Drawer
        anchor="top"
        open={activeDrawer === DrawerType.Search}
        onClose={() => {
          handleDrawerClose();
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.blur(); // Remove focus from the input
            }
          }, 0);
        }}
        disableEnforceFocus
        sx={{
          "&.MuiDrawer-root .MuiDrawer-paper": {
            marginTop: 100 - scrollPosition + "px",
          },
          zIndex: theme.zIndex.drawer,
        }}
      >
        <SearchMenu
          inputValue={inputValue}
          setInputValue={setInputValue}
          onInputChange={handleInputChange}
          filter={filter}
          setFilter={setFilter}
          handleDrawerClose={handleDrawerClose}
          alwaysShowInput
        />
      </Drawer>
    </Styled.HeaderContainer>
  );
};

export default Header;
